import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Search, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtMoney, ESTADOS } from "@/api";
import { useAuth } from "@/context/AuthContext";

const selCls = "h-10 px-3 bg-white border border-slate-300 rounded-md text-sm focus:border-emerald-600 outline-none";

function waLink(tel, nombre) {
  const num = (tel || "").replace(/\D/g, "");
  if (!num) return null;
  const n = num.startsWith("54") ? num : `549${num}`;
  return `https://wa.me/${n}?text=${encodeURIComponent(`Hola ${nombre || ""}, te contactamos de Seguros Para Moto por tu cotizacion.`)}`;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [filtros, setFiltros] = useState({ q: "", estado: "", tipo: "", etiqueta: "", incluir_ocultos: false });
  const [selIds, setSelIds] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p = page, f = filtros) => {
    setLoading(true);
    setSelIds([]);
    try {
      const params = new URLSearchParams({ page: p, limit: 25 });
      if (f.q) params.set("q", f.q);
      if (f.estado) params.set("estado", f.estado);
      if (f.tipo) params.set("tipo", f.tipo);
      if (f.etiqueta) params.set("etiqueta", f.etiqueta);
      if (f.incluir_ocultos) params.set("incluir_ocultos", "true");
      const [s, r] = await Promise.all([adminApi.get("/crm/stats"), adminApi.get(`/crm/operaciones?${params}`)]);
      setStats(s.data);
      setData(r.data);
    } finally {
      setLoading(false);
    }
  }, [page, filtros]);

  useEffect(() => { load(1, filtros); }, []);

  const limpiarLeads = async () => {
    try {
      const prev = await adminApi.post("/crm/operaciones/bulk-delete", { estado: "cotizado", confirmar: false });
      if (!prev.data.coincidentes) {
        toast.info("No hay cotizaciones sin emitir para eliminar");
        return;
      }
      if (!window.confirm(`Se eliminaran ${prev.data.coincidentes} cotizaciones que nunca llegaron a emitirse. Las operaciones emitidas y las polizas NO se tocan. Continuar?`)) return;
      const res = await adminApi.post("/crm/operaciones/bulk-delete", { estado: "cotizado", confirmar: true });
      toast.success(`${res.data.eliminadas} cotizaciones eliminadas`);
      setPage(1);
      load(1, filtros);
    } catch {
      toast.error("No se pudieron eliminar (requiere rol administrador)");
    }
  };

  const toggleOne = (id) => setSelIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = (checked) =>
    setSelIds((prev) =>
      checked
        ? [...new Set([...prev, ...data.items.map((o) => o.public_id)])]
        : prev.filter((id) => !data.items.some((o) => o.public_id === id))
    );

  const eliminarSeleccionados = async () => {
    if (!selIds.length) return;
    if (!window.confirm(`Se eliminaran ${selIds.length} operaciones seleccionadas (las polizas vigentes no se eliminan). Continuar?`)) return;
    try {
      const res = await adminApi.post("/crm/operaciones/bulk-delete", { ids: selIds, confirmar: true });
      toast.success(`${res.data.eliminadas} operaciones eliminadas${res.data.ignoradas ? ` (${res.data.ignoradas} omitidas: polizas vigentes no se eliminan)` : ""}`);
      setSelIds([]);
      setPage(1);
      load(1, filtros);
    } catch {
      toast.error("No se pudieron eliminar (requiere rol administrador)");
    }
  };

  const aplicarFiltroKpi = (f) => {
    const nf = { ...filtros, estado: f };
    setFiltros(nf);
    setPage(1);
    load(1, nf);
  };

  const kpis = stats ? [
    { label: "Cotizaciones", value: stats.cotizaciones, testid: "kpi-cotizaciones", f: "" },
    { label: "Polizas vigentes", value: stats.polizas, testid: "kpi-polizas", f: "poliza_vigente" },
    { label: "Conversion a poliza", value: `${stats.conversion}%`, testid: "kpi-conversion" },
    { label: "Emitidas sin pago", value: stats.pendientes_pago, testid: "kpi-pendientes", f: "pendiente_pago,emitido", alerta: stats.pendientes_pago > 0 },
    { label: "Pend. inspeccion", value: stats.pendientes_inspeccion, testid: "kpi-inspeccion", f: "pendiente_inspeccion" },
    { label: "Veces compartido", value: stats.compartidos, testid: "kpi-compartidos" },
  ] : [];

  const totalPages = Math.max(1, Math.ceil(data.total / 25));

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-bold text-slate-900">Cotizaciones y operaciones</h1>
        <div className="flex gap-2">
          {user?.role === "admin" && selIds.length > 0 && (
            <button data-testid="delete-selected-button" onClick={eliminarSeleccionados} className="flex items-center gap-2 text-sm text-white bg-red-600 rounded-md px-3 h-10 hover:bg-red-700 transition">
              <Trash2 className="w-4 h-4" /> Eliminar seleccionados ({selIds.length})
            </button>
          )}
          {user?.role === "admin" && (
            <button data-testid="bulk-delete-button" onClick={limpiarLeads} className="flex items-center gap-2 text-sm text-red-600 border border-red-200 rounded-md px-3 h-10 hover:bg-red-50 transition">
              <Trash2 className="w-4 h-4" /> Limpiar leads
            </button>
          )}
          <button data-testid="refresh-button" onClick={() => load()} className="flex items-center gap-2 text-sm text-slate-600 border border-slate-300 rounded-md px-3 h-10 hover:bg-white transition">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6">
        {kpis.map((k) => {
          const clickeable = k.f !== undefined;
          const activo = clickeable && filtros.estado === k.f;
          return (
            <button key={k.label} type="button" disabled={!clickeable} data-testid={k.testid}
              onClick={() => clickeable && aplicarFiltroKpi(k.f)}
              title={clickeable ? "Click para ver esas operaciones" : undefined}
              className={`bg-white border rounded-lg p-4 text-left transition ${clickeable ? "hover:shadow-md cursor-pointer" : "cursor-default"} ${activo ? "border-emerald-600 ring-2 ring-emerald-600/20" : "border-slate-200"}`}>
              <p className="text-xs text-slate-500 mb-1">{k.label}</p>
              <p className={`font-heading text-2xl font-bold ${k.alerta ? "text-amber-600" : "text-slate-900"}`}>{k.value}</p>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-52">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <input data-testid="search-input" placeholder="Buscar por nombre, DNI, patente u operacion" value={filtros.q}
            onChange={(e) => setFiltros({ ...filtros, q: e.target.value })}
            onKeyDown={(e) => e.key === "Enter" && load(1, filtros)}
            className="w-full h-10 pl-9 pr-3 bg-white border border-slate-300 rounded-md text-sm focus:border-emerald-600 outline-none" />
        </div>
        <select data-testid="filter-estado" className={selCls} value={filtros.estado} onChange={(e) => { setFiltros({ ...filtros, estado: e.target.value }); setTimeout(() => load(1, { ...filtros, estado: e.target.value }), 0); }}>
          <option value="">Todos los estados</option>
          {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select data-testid="filter-tipo" className={selCls} value={filtros.tipo} onChange={(e) => { setFiltros({ ...filtros, tipo: e.target.value }); setTimeout(() => load(1, { ...filtros, tipo: e.target.value }), 0); }}>
          <option value="">RC y Robo</option>
          <option value="rc">Responsabilidad Civil</option>
          <option value="robo">Con Robo</option>
        </select>
        <select data-testid="filter-etiqueta" className={selCls} value={filtros.etiqueta} onChange={(e) => { setFiltros({ ...filtros, etiqueta: e.target.value }); setTimeout(() => load(1, { ...filtros, etiqueta: e.target.value }), 0); }}>
          <option value="">Todas las etiquetas</option>
          {["Contactado", "Gestionado", "No pagó / Baja", "Pagado", "Seguimiento"].map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-slate-600 px-2 cursor-pointer" data-testid="filter-ocultos-label">
          <input type="checkbox" data-testid="filter-ocultos" className="w-4 h-4 accent-emerald-600" checked={filtros.incluir_ocultos}
            onChange={(e) => { setFiltros({ ...filtros, incluir_ocultos: e.target.checked }); setTimeout(() => load(1, { ...filtros, incluir_ocultos: e.target.checked }), 0); }} />
          Incluir ocultas
        </label>
        <button data-testid="search-button" onClick={() => load(1, filtros)} className="h-10 px-4 bg-slate-900 text-white rounded-md text-sm font-medium">Buscar</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm" data-testid="operaciones-table">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs text-slate-500 uppercase">
              {user?.role === "admin" && (
                <th className="p-3 w-10">
                  <input type="checkbox" data-testid="sel-todos" className="w-4 h-4 accent-emerald-600"
                    ref={(el) => { if (el) el.indeterminate = !data.items.every((o) => selIds.includes(o.public_id)) && data.items.some((o) => selIds.includes(o.public_id)); }}
                    checked={data.items.length > 0 && data.items.every((o) => selIds.includes(o.public_id))}
                    onChange={(e) => toggleAll(e.target.checked)} />
                </th>
              )}
              <th className="p-3">Fecha</th><th className="p-3">Cliente</th><th className="p-3">Moto</th><th className="p-3">Patente</th>
              <th className="p-3">Cobertura</th><th className="p-3 text-right">Premio</th><th className="p-3">Estado</th><th className="p-3">Etiqueta</th><th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((op) => {
              const est = ESTADOS[op.estado] || ESTADOS.cotizado;
              const wa = waLink(op.titular?.tel_cel, op.titular?.nombre);
              return (
                <tr key={op.public_id} className={`border-b border-slate-100 hover:bg-slate-50 ${selIds.includes(op.public_id) ? "bg-red-50/50" : ""}`} data-testid={`op-row-${op.public_id.slice(0, 8)}`}>
                  {user?.role === "admin" && (
                    <td className="p-3">
                      <input type="checkbox" data-testid={`sel-${op.public_id.slice(0, 8)}`} className="w-4 h-4 accent-emerald-600"
                        checked={selIds.includes(op.public_id)} onChange={() => toggleOne(op.public_id)} />
                    </td>
                  )}
                  <td className="p-3 text-slate-500 whitespace-nowrap" data-testid={`op-fecha-${op.public_id.slice(0, 8)}`}>
                    {op.created_at ? (
                      <>
                        {new Date(op.created_at).toLocaleDateString("es-AR", { timeZone: "America/Argentina/Buenos_Aires" })}
                        <span className="block text-xs text-slate-400">{new Date(op.created_at).toLocaleTimeString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", hour: "2-digit", minute: "2-digit", hour12: false })} hs</span>
                      </>
                    ) : "-"}
                  </td>
                  <td className="p-3">
                    {op.titular ? <><span className="font-medium text-slate-800">{op.titular.apellido} {op.titular.nombre}</span><span className="block text-xs text-slate-400">DNI {op.titular.nrodoc}</span></> : <span className="text-slate-400">Sin datos</span>}
                  </td>
                  <td className="p-3 text-slate-600">{op.vehiculo_cotizado?.marca_desc} {op.vehiculo_cotizado?.modelo_desc} {op.vehiculo_cotizado?.anio}</td>
                  <td className="p-3 font-data text-xs">{op.vehiculo?.patente || "-"}</td>
                  <td className="p-3 text-slate-600 max-w-44 truncate">{op.cobertura_elegida?.descripcion || "-"}</td>
                  <td className="p-3 text-right font-medium">{op.cobertura_elegida ? fmtMoney(op.cobertura_elegida.premio) : "-"}</td>
                  <td className="p-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ${est.cls}`}>{est.label}</span></td>
                  <td className="p-3">{op.etiqueta ? <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-700 whitespace-nowrap" data-testid={`etiqueta-${op.public_id.slice(0, 8)}`}>{op.etiqueta}</span> : <span className="text-slate-300 text-xs">-</span>}{op.oculto && <span className="ml-1 text-[10px] text-slate-400">(oculta)</span>}</td>
                  <td className="p-3 whitespace-nowrap">
                    <div className="flex gap-1">
                      {wa && <a data-testid={`wa-${op.public_id.slice(0, 8)}`} href={wa} target="_blank" rel="noreferrer" className="p-2 text-green-600 hover:bg-green-50 rounded-md" title="Contactar por WhatsApp"><MessageCircle className="w-4 h-4" /></a>}
                      <Link data-testid={`detalle-${op.public_id.slice(0, 8)}`} to={`/atm-seguros/operaciones/${op.public_id}`} className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-md text-xs font-medium">Ver</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!data.items.length && <tr><td colSpan={10} className="p-8 text-center text-slate-400" data-testid="empty-ops">No hay operaciones para los filtros elegidos</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4 text-sm text-slate-500">
        <span>{data.total} operaciones</span>
        <div className="flex gap-2">
          <button data-testid="page-prev" disabled={page <= 1} onClick={() => { setPage(page - 1); load(page - 1); }} className="px-3 h-9 border border-slate-300 rounded-md disabled:opacity-40 bg-white">Anterior</button>
          <span className="px-2 py-2">{page} / {totalPages}</span>
          <button data-testid="page-next" disabled={page >= totalPages} onClick={() => { setPage(page + 1); load(page + 1); }} className="px-3 h-9 border border-slate-300 rounded-md disabled:opacity-40 bg-white">Siguiente</button>
        </div>
      </div>
    </div>
  );
}
